// subscription.ts
import axiosInstance from "../../axiosInstance";

export interface Subscription {
  product_id: string;
  product_name: string;
  subscribed: boolean;
  start_date: string;
  end_date: string;
  on_trial?: boolean;
  days_left?: number;
}

const WEB_FRONTEND = "Web Frontend";
const GPLAN_API = "GPLAN API";

export const routeToProduct: Record<string, string> = {
  "/home": "*",
  "/inputeditor": "*",
  "/outputeditor": "*",
  "/api-keys": GPLAN_API,
};

const LOCAL_KEY = "subscribed_products";
const START_DATE_KEY = "subscribed_products_start_dates";
const END_DATE_KEY = "subscribed_products_end_dates";
const TRIAL_KEY = "trial_info";
const DEFAULT_TTL_MS = 2 * 60 * 1000; // 2 minutes

type StoredPayload = { value: string[]; expiresAt: number };

export function saveTrialInfo(
  subscriptions: Subscription[],
  ttlMs: number = DEFAULT_TTL_MS,
): void {
  const trialEntry = subscriptions.find((s) => s.on_trial === true);
  const payload = {
    value: {
      onTrial: !!trialEntry,
      daysLeft: trialEntry?.days_left ?? 0,
    },
    expiresAt: Date.now() + ttlMs,
  };
  localStorage.setItem(TRIAL_KEY, JSON.stringify(payload));
}

export function getTrialInfo(): { onTrial: boolean; daysLeft: number } {
  const raw = localStorage.getItem(TRIAL_KEY);
  if (!raw) return { onTrial: false, daysLeft: 0 };
  try {
    const payload = JSON.parse(raw);
    if (Date.now() > payload.expiresAt) {
      localStorage.removeItem(TRIAL_KEY);
      return { onTrial: false, daysLeft: 0 };
    }
    return payload.value ?? { onTrial: false, daysLeft: 0 };
  } catch {
    return { onTrial: false, daysLeft: 0 };
  }
}

export function saveSubscribedProductNames(
  subscriptions: Subscription[],
  ttlMs: number = DEFAULT_TTL_MS,
): void {
  const isOnTrial = subscriptions.some((s) => s.on_trial === true);
  const productSubs = subscriptions.filter((s) => s.product_name);
  const activeSubs = isOnTrial
    ? productSubs
    : productSubs.filter((s) => s.subscribed);
  const activeNames = activeSubs.map((s) => s.product_name);
  const startDates = activeSubs.map((s) => s.start_date);
  const endDates = activeSubs.map((s) => s.end_date);

  const payloadNames: StoredPayload = {
    value: activeNames,
    expiresAt: Date.now() + ttlMs,
  };

  const payloadStartDates: StoredPayload = {
    value: startDates,
    expiresAt: Date.now() + ttlMs,
  };

  const payloadEndDates: StoredPayload = {
    value: endDates,
    expiresAt: Date.now() + ttlMs,
  };

  localStorage.setItem(LOCAL_KEY, JSON.stringify(payloadNames));
  localStorage.setItem(START_DATE_KEY, JSON.stringify(payloadStartDates));
  localStorage.setItem(END_DATE_KEY, JSON.stringify(payloadEndDates));
}

export function getSubscribedProductNames(): string[] {
  const raw = localStorage.getItem(LOCAL_KEY);
  if (!raw) return [];

  try {
    const payload = JSON.parse(raw) as StoredPayload;
    if (Date.now() > payload.expiresAt) {
      localStorage.removeItem(LOCAL_KEY);
      return [];
    }
    return payload.value ?? [];
  } catch {
    return [];
  }
}

export function getSubscribedStartDates(): string[] {
  const raw = localStorage.getItem(START_DATE_KEY);
  if (!raw) return [];

  try {
    const payload = JSON.parse(raw) as StoredPayload;
    if (Date.now() > payload.expiresAt) {
      localStorage.removeItem(START_DATE_KEY);
      return [];
    }
    return payload.value ?? [];
  } catch {
    return [];
  }
}

export function getSubscribedEndDates(): string[] {
  const raw = localStorage.getItem(END_DATE_KEY);
  if (!raw) return [];

  try {
    const payload = JSON.parse(raw) as StoredPayload;
    if (Date.now() > payload.expiresAt) {
      localStorage.removeItem(END_DATE_KEY);
      return [];
    }
    return payload.value ?? [];
  } catch {
    return [];
  }
}

/**
 * Fetch subscription list from backend and update local cache.
 */
export async function fetchAndSaveSubscriptionsFromServer(): Promise<string[]> {
  const token = localStorage.getItem("access_token");

  const res = await axiosInstance.get<{ subscriptions: Subscription[] }>(
    `${process.env.REACT_APP_BASEURL}/users/subscriptions/status/`,
  );

  const subs = res.data?.subscriptions ?? [];
  saveTrialInfo(subs);
  saveSubscribedProductNames(subs);
  const isOnTrial = subs.some((s) => s.on_trial === true);
  const productSubs = subs.filter((s) => s.product_name);
  return isOnTrial
    ? productSubs.map((s) => s.product_name)
    : productSubs.filter((s) => s.subscribed).map((s) => s.product_name);
}
